import { useQuery } from '@apollo/client';
import { graphql, useStaticQuery } from 'gatsby';
import React, { FC, useContext } from 'react';

import LoadingWrapper from '@ui/atoms/LoadingWrapper';

import { keysToCamel } from '@utils/butter';
import { exists } from '@utils/general';

import FlowContext from '@providers/FlowContextProvider';
import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { IServiceWaivable } from '@data/insurance';

import { IFlowStepProps } from '@folx-types/flow-steps';

import isMembershipWaivableQuery from '@graphql/queries/isMembershipWaivable.query.gql';
import providerInNetworkQuery from '@graphql/queries/providerInNetwork.query.gql';

import MembershipInsuranceStatusStep, {
	IMembershipInsuranceStatusStepPageContent,
} from './MembershipInsuranceStatusStep.component';

interface IMembershipInsuranceStatusStepPageProps extends IFlowStepProps {}

interface IMembershipWaivableResponse {
	authenticatedMember: {
		isMembershipWaivable: IServiceWaivable;
	};
}

interface IProviderInNetworkResponse {
	authenticatedMember: {
		providerInNetwork: boolean;
	};
}

const MembershipInsuranceStatusStepPage: FC<IMembershipInsuranceStatusStepPageProps> = ({
	path,
	stepKey,
}) => {
	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "membership-insurance-status-step" }) {
				in_network_header
				in_network_description
				in_network_covered_description
				out_network_header
				out_network_description
			}
		}
	`);

	const { existingFormData, handleFormSubmission, handleNavBack } = useContext(FlowContext);

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);
	const { loading: isMembershipWaivableLoading, data: isMembershipWaivableData } =
		useQuery<IMembershipWaivableResponse>(isMembershipWaivableQuery, {
			client: graphQLv2Client,
			variables: {
				insuranceId: exists(existingFormData?.memberInsuranceId)
					? existingFormData?.memberInsuranceId
					: null,
				stateOfResidence: existingFormData?.shippingState,
				dob: existingFormData?.dob,
			},
			fetchPolicy: 'cache-and-network',
		});

	const { loading: providerInNetworkLoading, data: providerInNetworkData } =
		useQuery<IProviderInNetworkResponse>(providerInNetworkQuery, {
			client: graphQLv2Client,
			variables: {
				insuranceId: exists(existingFormData?.memberInsuranceId)
					? existingFormData?.memberInsuranceId
					: null,
			},
			fetchPolicy: 'cache-and-network',
		});

	const isWaivable =
		!!isMembershipWaivableData?.authenticatedMember?.isMembershipWaivable?.isWaivable;

	const inNetwork = !!providerInNetworkData?.authenticatedMember?.providerInNetwork;

	const refinedContent: IMembershipInsuranceStatusStepPageContent = keysToCamel(
		pageContent
	) as IMembershipInsuranceStatusStepPageContent;

	const loading = isMembershipWaivableLoading || providerInNetworkLoading;
	return (
		<LoadingWrapper loading={loading}>
			<MembershipInsuranceStatusStep
				pageContent={refinedContent}
				path={path}
				stepKey={stepKey}
				existingFormData={existingFormData}
				handleFormSubmission={handleFormSubmission}
				handleNavBack={handleNavBack}
				serviceWaivable={{
					...isMembershipWaivableData?.authenticatedMember?.isMembershipWaivable,
					isWaivable,
					inNetwork,
				}}
			/>
		</LoadingWrapper>
	);
};

export default MembershipInsuranceStatusStepPage;
